import { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, FormControl, FormControlLabel, IconButton, InputLabel,
  MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import {
  ICreateSurveyQuestion,
  ICreateSurveyQuestionOption,
  ISurveyQuestion,
  ISurveyQuestionOption,
  ISurveyQuestionType,
} from '../../../shared/services/survey/survey.model';
import { notifyError } from '../../../shared/utils/notify-error';
import { useDirtyBaseline } from '../../../hooks/use-dirty-baseline';

/**
 * The eight stored question types presented as six families: the two choice
 * families each carry a display-style choice (dropdown vs radio / checkboxes),
 * because those pairs differ only in how the respondent sees them.
 */
type TypeFamily = 'TEXT_SINGLE_LINE' | 'TEXT_MULTI_LINE' | 'NUMBER' | 'DATE' | 'CHOICE_SINGLE' | 'CHOICE_MULTI';

const FAMILY_LABELS: { family: TypeFamily; label: string }[] = [
  { family: 'TEXT_SINGLE_LINE', label: 'Text (one line)' },
  { family: 'TEXT_MULTI_LINE', label: 'Text (paragraph)' },
  { family: 'NUMBER', label: 'Number' },
  { family: 'DATE', label: 'Date' },
  { family: 'CHOICE_SINGLE', label: 'Choice (pick one)' },
  { family: 'CHOICE_MULTI', label: 'Choice (pick many)' },
];

function familyOfCode(code: string | undefined): TypeFamily {
  switch (code) {
    case 'SELECT_SINGLE':
    case 'RADIO':
      return 'CHOICE_SINGLE';
    case 'SELECT_MULTI':
    case 'CHECKBOX_GROUP':
      return 'CHOICE_MULTI';
    case 'TEXT_MULTI_LINE':
    case 'NUMBER':
    case 'DATE':
      return code;
    default:
      return 'TEXT_SINGLE_LINE';
  }
}

function codeFor(family: TypeFamily, choiceStyle: 'dropdown' | 'buttons'): string {
  if (family === 'CHOICE_SINGLE') return choiceStyle === 'dropdown' ? 'SELECT_SINGLE' : 'RADIO';
  if (family === 'CHOICE_MULTI') return choiceStyle === 'dropdown' ? 'SELECT_MULTI' : 'CHECKBOX_GROUP';
  return family;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Receives the saved question's id, so hosts can add a freshly created question somewhere. */
  successHandler: (savedQuestionId?: string) => void;
  questionTypes: ISurveyQuestionType[];
  existingQuestion?: ISurveyQuestion;
  // The partner Library authors the same questions against its own tables. Everything else in
  // this dialog -- the type list, the option editor, the numeric bounds -- is identical, so
  // injecting the four save calls is the whole of the difference.
  api?: {
    createQuestion: (payload: any) => Promise<any>;
    updateQuestion: (id: string, payload: any) => Promise<any>;
    addOption: (questionId: string, payload: any) => Promise<any>;
    deleteOption: (questionId: string, optionId: string) => Promise<any>;
  };
}

export const SurveyQuestionDialog = ({ open, onClose, successHandler, questionTypes, existingQuestion, api }: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const saveApi = api ?? {
    createQuestion: (payload: any) => surveyAdminService.createQuestion(payload),
    updateQuestion: (id: string, payload: any) => surveyAdminService.updateQuestion(id, payload),
    addOption: (questionId: string, payload: any) => surveyAdminService.addOption(questionId, payload),
    deleteOption: (questionId: string, optionId: string) => surveyAdminService.deleteOption(questionId, optionId),
  };
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [helpText, setHelpText] = useState('');
  const [collectMoreDetails, setCollectMoreDetails] = useState(false);
  const [enforceNumericAnswer, setEnforceNumericAnswer] = useState(false);
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [family, setFamily] = useState<TypeFamily>('TEXT_SINGLE_LINE');
  const [choiceStyle, setChoiceStyle] = useState<'dropdown' | 'buttons'>('dropdown');
  const [options, setOptions] = useState<Partial<ISurveyQuestionOption>[]>([]);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [newOptionNumericValue, setNewOptionNumericValue] = useState('');

  // Dirty tracking over every user-editable field (hand-rolled state, so no formik.dirty).
  // The initialisation effect below calls rebaseline() after seeding.
  const { isDirty, rebaseline } = useDirtyBaseline({
    prompt, helpText, collectMoreDetails, enforceNumericAnswer, minValue, maxValue,
    isRequired, family, choiceStyle, options, newOptionLabel, newOptionNumericValue,
  });

  const isEditMode = !!existingQuestion;
  const isChoiceFamily = family === 'CHOICE_SINGLE' || family === 'CHOICE_MULTI';
  const selectedCode = codeFor(family, choiceStyle);
  const surveyQuestionTypeId = questionTypes.find(t => t.code === selectedCode)?.id ?? '';
  const selectedType = questionTypes.find(t => t.id === surveyQuestionTypeId);
  const canEnforceNumeric =
    selectedType?.code === 'TEXT_SINGLE_LINE' || selectedType?.code === 'TEXT_MULTI_LINE';
  // True when the question will produce/accept a numeric value — either a dedicated NUMBER type,
  // or a text type with the "Numbers only" flag enabled.
  const isNumericQuestion = selectedType?.code === 'NUMBER' || (canEnforceNumeric && enforceNumericAnswer);

  useEffect(() => {
    if (open && existingQuestion) {
      setPrompt(existingQuestion.prompt);
      setHelpText(existingQuestion.helpText ?? '');
      setCollectMoreDetails(existingQuestion.collectMoreDetails ?? false);
      setEnforceNumericAnswer(existingQuestion.enforceNumericAnswer ?? false);
      setMinValue(existingQuestion.minValue != null ? String(existingQuestion.minValue) : '');
      setMaxValue(existingQuestion.maxValue != null ? String(existingQuestion.maxValue) : '');
      setIsRequired(existingQuestion.isRequired);
      const existingCode = questionTypes.find(t => t.id === existingQuestion.surveyQuestionTypeId)?.code;
      setFamily(familyOfCode(existingCode));
      setChoiceStyle(existingCode === 'RADIO' || existingCode === 'CHECKBOX_GROUP' ? 'buttons' : 'dropdown');
      setOptions(existingQuestion.surveyQuestionOptions ?? []);
    } else if (open && !existingQuestion) {
      setPrompt('');
      setHelpText('');
      setCollectMoreDetails(false);
      setEnforceNumericAnswer(false);
      setMinValue('');
      setMaxValue('');
      setIsRequired(false);
      setFamily('TEXT_SINGLE_LINE');
      setChoiceStyle('dropdown');
      setOptions([]);
    }
    setNewOptionLabel('');
    setNewOptionNumericValue('');
    // Full snapshot is safe here: this effect runs synchronously in the open transition,
    // before the user can have touched the form.
    rebaseline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingQuestion, questionTypes]);

  const handleAddOption = () => {
    if (!newOptionLabel.trim()) return;
    const parsedNumeric = newOptionNumericValue.trim() !== '' ? Number(newOptionNumericValue.trim()) : undefined;
    setOptions(prev => [...prev, {
      label: newOptionLabel.trim(),
      value: newOptionLabel.trim().toLowerCase().replace(/\s+/g, '_'),
      numericValue: parsedNumeric != null && Number.isFinite(parsedNumeric) ? parsedNumeric : null,
      sortOrder: prev.length,
    }]);
    setNewOptionLabel('');
    setNewOptionNumericValue('');
  };

  const handleRemoveOption = async (option: Partial<ISurveyQuestionOption>, index: number) => {
    if (isEditMode && existingQuestion && option.id) {
      const removedOptionId = option.id;
      try {
        await saveApi.deleteOption(existingQuestion.id, removedOptionId);
      } catch (err) {
        notifyError(userNotificationsContext.openSnackbar, err, 'removing the option');
        return;
      }
      // The removal is already persisted, so it must not count as an unsaved change:
      // splice just this entry out of the baseline. Snapshotting the whole list here
      // would absorb unsaved local additions (options added but not yet submitted).
      rebaseline((currentBaseline) => {
        const baseline = currentBaseline as { options?: Partial<ISurveyQuestionOption>[] };
        return {
          ...baseline,
          options: (baseline.options ?? []).filter(existing => existing.id !== removedOptionId),
        };
      });
    }
    setOptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!prompt.trim() || !surveyQuestionTypeId) return;
    setIsSubmitting(true);
    try {
      const payload: ICreateSurveyQuestion = {
        prompt: prompt.trim(),
        helpText: helpText.trim() || undefined,
        isRequired,
        collectMoreDetails,
        enforceNumericAnswer: canEnforceNumeric ? enforceNumericAnswer : false,
        minValue: (isNumericQuestion && minValue.trim() !== '' && Number.isFinite(Number(minValue))) ? Number(minValue) : null,
        maxValue: (isNumericQuestion && maxValue.trim() !== '' && Number.isFinite(Number(maxValue))) ? Number(maxValue) : null,
        surveyQuestionTypeId,
      };

      let questionId: string;
      if (isEditMode && existingQuestion) {
        await saveApi.updateQuestion(existingQuestion.id, payload);
        questionId = existingQuestion.id;
      } else {
        const created = await saveApi.createQuestion(payload);
        questionId = created.id;
      }

      // Add new options that don't have an id yet
      if (selectedType?.allowsOptions) {
        const newOptions = options.filter(o => !o.id);
        for (const opt of newOptions) {
          const optPayload: ICreateSurveyQuestionOption = {
            label: opt.label!,
            value: opt.value!,
            numericValue: opt.numericValue ?? null,
            sortOrder: opt.sortOrder ?? 0,
          };
          await saveApi.addOption(questionId, optPayload);
        }
      }

      userNotificationsContext.openSnackbar({
        open: true,
        message: isEditMode ? 'Question updated' : 'Question created',
        backgroundColor: 'primary.dark',
      });
      successHandler(questionId);
      onClose();
    } catch (err) {
      notifyError(userNotificationsContext.openSnackbar, err, 'saving the question');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Question' : 'Create Question'}
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!prompt.trim() || !surveyQuestionTypeId}
      isDirty={isDirty}
      maxWidth='sm'
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          label='Question prompt'
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          fullWidth
          required
          multiline
          rows={2}
        />
        <TextField
          label='Help text (optional)'
          value={helpText}
          onChange={e => setHelpText(e.target.value)}
          fullWidth
        />
        <FormControl fullWidth>
          <InputLabel>Question type</InputLabel>
          <Select
            value={family}
            label='Question type'
            onChange={e => {
              const nextFamily = e.target.value as TypeFamily;
              setFamily(nextFamily);
              if (nextFamily !== 'TEXT_SINGLE_LINE' && nextFamily !== 'TEXT_MULTI_LINE') {
                setEnforceNumericAnswer(false);
              }
            }}
            disabled={isEditMode}
          >
            {FAMILY_LABELS.map(f => (
              <MenuItem key={f.family} value={f.family}>{f.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {isChoiceFamily && (
          <FormControl fullWidth size='small'>
            <InputLabel>Displayed as</InputLabel>
            <Select
              value={choiceStyle}
              label='Displayed as'
              onChange={e => setChoiceStyle(e.target.value as 'dropdown' | 'buttons')}
              disabled={isEditMode}
            >
              <MenuItem value='dropdown'>Dropdown</MenuItem>
              <MenuItem value='buttons'>{family === 'CHOICE_SINGLE' ? 'Button list' : 'Checkboxes'}</MenuItem>
            </Select>
          </FormControl>
        )}
        {isEditMode && (
          <Typography variant='caption' color='text.secondary'>
            The question type is fixed after creation because saved answers depend on it.
          </Typography>
        )}
        <FormControlLabel
          control={<Checkbox checked={isRequired} onChange={e => setIsRequired(e.target.checked)} />}
          label='Required'
        />
        <FormControlLabel
          control={<Checkbox checked={collectMoreDetails} onChange={e => setCollectMoreDetails(e.target.checked)} />}
          label='Show optional "Please provide more details" text box'
        />
        {canEnforceNumeric && (
          <FormControlLabel
            control={<Checkbox checked={enforceNumericAnswer} onChange={e => setEnforceNumericAnswer(e.target.checked)} />}
            label='Numeric answer (numbers only)'
          />
        )}

        {/* Whether this question can feed calculations is decided here at authoring time;
            surfacing it now avoids a dead-end discovery later in the rule dialogs. */}
        {(() => {
          if (family === 'NUMBER' || (canEnforceNumeric && enforceNumericAnswer)) {
            return (
              <Typography variant='caption' color='success.main'>
                This question can be used in calculations.
              </Typography>
            );
          }
          if (canEnforceNumeric) {
            return (
              <Typography variant='caption' color='text.secondary'>
                Text answers cannot be used in calculations. Choose Number, or turn on numeric answers.
              </Typography>
            );
          }
          if (isChoiceFamily) {
            const allHaveValues = options.length > 0 && options.every(
              o => o.numericValue != null && Number.isFinite(Number(o.numericValue)),
            );
            return allHaveValues ? (
              <Typography variant='caption' color='success.main'>
                This question can be used in calculations.
              </Typography>
            ) : (
              <Typography variant='caption' color='text.secondary'>
                To use this question in calculations, give every option a calculation value below.
              </Typography>
            );
          }
          return null;
        })()}
        {isNumericQuestion && (
          <Stack direction='row' spacing={2}>
            <TextField
              size='small'
              label='Min value (optional)'
              type='number'
              value={minValue}
              onChange={e => setMinValue(e.target.value)}
              sx={{ flex: 1 }}
              inputProps={{ step: 'any' }}
            />
            <TextField
              size='small'
              label='Max value (optional)'
              type='number'
              value={maxValue}
              onChange={e => setMaxValue(e.target.value)}
              sx={{ flex: 1 }}
              inputProps={{ step: 'any' }}
            />
          </Stack>
        )}

        {selectedType?.allowsOptions && (
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>Options</Typography>
            {options.map((opt, index) => (
              <Stack key={index} direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
                <Typography variant='body2' sx={{ flex: 1 }}>{opt.label}</Typography>
                {opt.numericValue != null && (
                  <Typography variant='caption' color='primary.main' sx={{ fontFamily: 'monospace' }}>
                    = {opt.numericValue}
                  </Typography>
                )}
                <IconButton size='small' onClick={() => handleRemoveOption(opt, index)}>
                  <DeleteOutline fontSize='small' />
                </IconButton>
              </Stack>
            ))}
            <Stack direction='row' spacing={1} sx={{ mt: 1 }} flexWrap='wrap' rowGap={1}>
              <TextField
                size='small'
                label='Label'
                value={newOptionLabel}
                onChange={e => setNewOptionLabel(e.target.value)}
                sx={{ flex: 2, minWidth: 120 }}
              />
              <TextField
                size='small'
                label='Calculation value (optional)'
                type='number'
                value={newOptionNumericValue}
                onChange={e => setNewOptionNumericValue(e.target.value)}
                sx={{ flex: 1, minWidth: 100 }}
                inputProps={{ step: 'any' }}
              />
              <Button
                variant='outlined'
                size='small'
                startIcon={<AddOutlined />}
                onClick={handleAddOption}
                disabled={!newOptionLabel.trim()}
              >
                Add
              </Button>
            </Stack>
            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
              Calculation value: the number this option stands for in calculations, for example Diesel = 2.68.
            </Typography>
          </Box>
        )}
      </Stack>
    </DialogWrapper>
  );
};
